/**
 * PoGo Pokédex grid slot → National Dex id mapping (Build 2 / v1.4.6).
 *
 * Bug this fixes: the live scanner treated a Pokémon GO Pokédex grid slot
 * position as if it were the National Dex id directly. That happens to be
 * correct on the Kanto tab (slot 1 = #1 Bulbasaur) but is WRONG on every
 * later region tab, because Pokémon GO restarts the *visual* slot numbering
 * at 1 inside each region tab while the National Dex id keeps climbing
 * (Johto slot 1 is #152 Chikorita, not #1). The device log showing clean
 * sequential ids like [1,2,3,4] / [37,38,39,40] for what should have been a
 * Johto/Hoenn scan was the tell.
 *
 * Mapping model
 * -------------
 * Within a single region TAB, Pokémon GO lists that region's species in
 * ascending National Dex order with no gaps for the base forms the dex grid
 * shows (forms / regional variants / costumes are surfaced on the detail
 * page, not as extra grid slots). So the per-region mapping is a simple
 * affine transform:
 *
 *     dexId = regionStart(region) + (slotIndex - 1)     // slotIndex 1-based
 *
 * where regionStart comes from the canonical National Dex region ranges in
 * `types.ts` (the same table DexView / Compare already trust). We clamp to
 * the region's [start, end] range and return null for an out-of-range slot
 * rather than emitting a bogus id.
 *
 * Source for the region boundaries: the National Dex generation ranges
 * (Bulbapedia "List of Pokémon by National Pokédex number") as encoded in
 * `REGIONS` in client/src/lib/types.ts. Kanto 1-151, Johto 152-251,
 * Hoenn 252-386, … — see that table for the full list, kept in one place so
 * this mapping and the rest of the app never drift.
 *
 * Pure + exportable so it is unit-testable in isolation (see
 * pogoSlotMapping.test.ts).
 */

import { REGIONS, regionForId, type Region } from "./types";

/** A region the live scanner can resolve a slot mapping for. */
export type SlotMappableRegion = Region;

const REGION_RANGE: Record<Region, [number, number]> = Object.fromEntries(
  REGIONS.map((r) => [r.key, r.range]),
) as Record<Region, [number, number]>;

/**
 * First National Dex id of a region tab (the id its slot 1 maps to).
 * Kanto → 1, Johto → 152, Hoenn → 252, …
 */
export function regionStartDexId(region: Region): number {
  return REGION_RANGE[region][0];
}

/**
 * Translate a Pokémon GO grid slot (1-based, within the given region tab) to
 * its National Dex id. Returns null when:
 *   - the region is unknown / unmappable, or
 *   - the resulting id falls outside that region's National Dex range
 *     (a misread slot index — better to drop it than corrupt a neighbour).
 */
export function pogoSlotToNationalDex(
  region: string,
  slotIndex: number,
): number | null {
  if (!Number.isFinite(slotIndex) || slotIndex < 1) return null;
  const range = REGION_RANGE[region as Region];
  if (!range) return null;
  const [start, end] = range;
  const id = start + Math.round(slotIndex) - 1;
  if (id < start || id > end) return null;
  return id;
}

/**
 * Resolve the National Dex id for a scanned grid cell.
 *
 * Pokémon GO prints the National Dex number ("#0152") inside each cell, so
 * when OCR read a number that is already inside the active region's range we
 * trust it verbatim — that is the most accurate signal available. We only
 * fall back to the slot-position mapping when the OCR'd number is missing or
 * is inconsistent with the region (the classic "slot index used as id" bug,
 * e.g. region=johto but ocrNumber=1). In that case we recompute the id from
 * the cell's slot position so a Johto slot-1 cell becomes #152, not #1.
 *
 * `slotIndex` is the cell's 1-based position within the region tab. For a
 * single visible 4×4 page that is `rowIndex * 4 + colIndex + 1`; callers that
 * track scroll offset can pass an absolute slot.
 *
 * Returns `{ dexId, source }` or null when nothing resolves.
 */
export function resolveCellNationalDex(args: {
  region: string;
  slotIndex: number;
  ocrNumber: number | null;
}): { dexId: number; source: "ocr" | "slot-map" } | null {
  const { region, slotIndex, ocrNumber } = args;
  const range = REGION_RANGE[region as Region];

  // Region we can't map (e.g. "unknown"): trust a positive OCR number, else
  // give up — we will not invent an id without a region anchor.
  if (!range) {
    if (ocrNumber != null && Number.isFinite(ocrNumber) && ocrNumber >= 1) {
      return { dexId: ocrNumber, source: "ocr" };
    }
    return null;
  }

  const [start, end] = range;
  // OCR'd a number that is already a valid id for this region → trust it.
  if (ocrNumber != null && ocrNumber >= start && ocrNumber <= end) {
    return { dexId: ocrNumber, source: "ocr" };
  }
  // Stale-region recovery (telemetry 2026-06-23). The printed "#0152" digits are
  // the authoritative cell id. When OCR read a number ABOVE this region's range
  // that is itself a real National Dex id, the *active region context is stale*
  // (e.g. a Shadow→Purified switch that kept the kanto/top-row context while the
  // PoGo page actually shows Johto 152-158), NOT a slot-index-as-id misread.
  //
  // The slot-index-as-id bug this fallback exists for ALWAYS reads BELOW the
  // region start (region=johto, ocrNumber=1 — the visual slot number). Only that
  // direction needs slot-map recovery. A higher, valid printed id must be trusted
  // as OCR-sourced so a genuine catch produces a real OCR observation keyed to
  // the printed dex number instead of a fabricated slot-map id that the
  // provenance gate then blocks (the "nothing applied" failure). Numbers above
  // the whole National Dex (OCR garbage) still fall through to slot-map / drop.
  if (
    ocrNumber != null &&
    Number.isFinite(ocrNumber) &&
    ocrNumber > end &&
    regionForId(Math.round(ocrNumber)) != null
  ) {
    return { dexId: Math.round(ocrNumber), source: "ocr" };
  }
  // Otherwise the number is missing or below the region start (the slot-as-id
  // bug). Recover from the slot position.
  const mapped = pogoSlotToNationalDex(region, slotIndex);
  if (mapped != null) return { dexId: mapped, source: "slot-map" };
  return null;
}
